import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useSettings } from '../context/SettingsContext';
import { usePageTracking } from '../hooks/usePageTracking';
import { LayoutDashboard, Users, Wallet, Package, FileText, TrendingUp, History, FileBox, Mail, Bell, LogOut, Menu, X, Palette, FileSpreadsheet, DollarSign, ClipboardList, Globe, Sun, Moon, Eye, Shield, Briefcase, Settings, UserCog, Calculator, Newspaper, Radio, BarChart3, Download, Building2, CreditCard, Banknote, ArrowRightLeft, ChevronDown, User, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import SessionWarning from './SessionWarning';
import DashboardLayoutV3 from '../pages/client/v3/DashboardLayoutV3';
import DashboardLayoutV4 from '../pages/client/v4/DashboardLayoutV4';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DashboardLayout = ({ children, hideSidebar = false }) => {
  const { user, logout, token, mirrorMode, exitMirrorMode } = useAuth();
  const { branding, currentTemplate, isDark, toggleDarkMode } = useTheme();
  const { t, language, changeLanguage } = useLanguage();
  const { clientTemplate, platformSettings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLegalPopup, setShowLegalPopup] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [liveClients, setLiveClients] = useState([]);
  const [showLiveDropdown, setShowLiveDropdown] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({ pending_transactions: 0, pending_withdrawals: 0, unread_messages: 0 });
  const [officePermissions, setOfficePermissions] = useState(null);
  const [clientCode, setClientCode] = useState('');

  const getHomeRoute = () => {
    if (user?.role === 'client') return '/client/dashboard';
    if (user?.role === 'analyst') return '/analyst/dashboard';
    if (user?.role === 'super_admin' || user?.role === 'admin_ops' || user?.role === 'bureau') return '/admin/dashboard';
    return '/login';
  };

  // Track page navigation
  usePageTracking();

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadNotifications(response.data.count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token]);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_ops' || user?.role === 'bureau' || user?.role === 'analyst';

  const fetchLiveClients = useCallback(async () => {
    if (!token || !isAdmin) return;
    try {
      const response = await axios.get(`${API}/tracking/clients-live`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveClients(response.data.clients || []);
    } catch (error) {
      console.debug('Failed to fetch live clients:', error);
    }
  }, [token, isAdmin]);

  const fetchPendingCounts = useCallback(async () => {
    if (!token) return;
    try {
      if (user?.role === 'client') {
        // Client: only fetch unread messages count
        const res = await axios.get(`${API}/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
        setPendingCounts({ pending_transactions: 0, pending_withdrawals: 0, unread_messages: res.data?.count || 0 });
      } else {
        const res = await axios.get(`${API}/stats/pending-counts`, { headers: { Authorization: `Bearer ${token}` } });
        setPendingCounts(res.data || { pending_transactions: 0, pending_withdrawals: 0, unread_messages: 0 });
      }
    } catch {}
  }, [token, user?.role]);

  useEffect(() => {
    fetchUnreadCount();
    fetchLiveClients();
    fetchPendingCounts();
    // Load office permissions for bureau admins
    if (user?.office_id && token) {
      axios.get(`${API}/offices/my-permissions`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setOfficePermissions(res.data)).catch(() => {});
    }
    // Load client code for client header
    if (user?.role === 'client' && token && user?.id) {
      axios.get(`${API}/clients/${user.id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setClientCode(res.data?.client_code || ''))
        .catch(() => {});
    }
    const interval = setInterval(() => { fetchUnreadCount(); fetchLiveClients(); fetchPendingCounts(); }, 15000);
    const onNotifUpdate = () => { fetchUnreadCount(); fetchPendingCounts(); };
    window.addEventListener('notifications-updated', onNotifUpdate);
    return () => { clearInterval(interval); window.removeEventListener('notifications-updated', onNotifUpdate); };
  }, [fetchUnreadCount, fetchLiveClients, fetchPendingCounts, user?.office_id, user?.role, token]);

  const getNavItems = () => {
    const role = user?.role;
    if (role === 'super_admin' || role === 'admin_ops' || role === 'bureau') {
      let items = [];
      
      if (role === 'super_admin') {
        items = [
          // --- Navigation principale ---
          { href: '/admin', icon: LayoutDashboard, label: t('dashboard') },
          { href: '/admin/clients', icon: Users, label: t('clients') },
          { href: '/admin/analysts', icon: UserCog, label: 'Analystes' },
          { href: '/admin/managers', icon: Shield, label: 'Admins' },
          { href: '/admin/offices', icon: Building2, label: 'Bureaux' },
          // --- Finances ---
          { href: '/admin/statistics', icon: BarChart3, label: 'Statistiques' },
          { href: '/admin/placements', icon: Briefcase, label: 'Placements' },
          { href: '/admin/transactions', icon: DollarSign, label: 'Transactions' },
          { href: '/admin/withdrawals', icon: Wallet, label: 'Retraits' },
          { href: '/admin/payments', icon: CreditCard, label: 'Paiements' },
          { href: '/admin/wallet-requests', icon: Wallet, label: 'Wallets' },
          { href: '/admin/advances', icon: Banknote, label: 'Avances de fonds' },
          { href: '/admin/mandates', icon: FileText, label: 'Mandats de gestion' },
          { href: '/admin/iban-pool', icon: CreditCard, label: 'Gestion RIB' },
          // --- Communication ---
          { href: '/admin/news', icon: Newspaper, label: 'Actualités' },
          { href: '/messages', icon: Mail, label: t('messages') },
          { href: '/admin/whatsapp-templates', icon: MessageSquare, label: 'WhatsApp Templates' },
          // --- Paramètres ---
          { href: '/admin/template', icon: FileSpreadsheet, label: t('template') },
          { href: '/admin/branding', icon: Palette, label: t('branding') },
          { href: '/admin/security', icon: Shield, label: 'Sécurité' },
          { href: '/admin/audit', icon: ClipboardList, label: t('audit') },
          { href: '/admin/settings', icon: Settings, label: 'Paramètres' },
        ];
      } else {
        // Admin Ops or Bureau (office-bound)
        const isOfficeBound = !!user?.office_id;
        const isBureau = role === 'bureau';
        const perms = user?.office_permissions || {};
        items = [
          { href: '/admin', icon: LayoutDashboard, label: t('dashboard') },
          { href: '/admin/clients', icon: Users, label: t('clients') },
          { href: '/admin/analysts', icon: UserCog, label: 'Analystes' },
        ];
        if (!isOfficeBound) {
          items.push({ href: '/admin/offices', icon: Building2, label: 'Bureaux' });
        }
        items.push({ href: '/admin/statistics', icon: BarChart3, label: 'Statistiques' });
        items.push({ href: '/admin/placements', icon: Briefcase, label: 'Placements' });
        items.push({ href: '/admin/transactions', icon: DollarSign, label: 'Transactions' });
        items.push({ href: '/admin/withdrawals', icon: Wallet, label: 'Retraits' });
        items.push({ href: '/admin/payments', icon: CreditCard, label: 'Paiements' });
        items.push({ href: '/admin/wallet-requests', icon: Wallet, label: 'Wallets' });
        items.push({ href: '/admin/advances', icon: Banknote, label: 'Avances de fonds' });
        items.push({ href: '/admin/mandates', icon: FileText, label: 'Mandats de gestion' });
        items.push({ href: '/admin/iban-pool', icon: CreditCard, label: 'Gestion RIB' });
        items.push({ href: '/admin/news', icon: Newspaper, label: 'Actualités' });
        items.push({ href: '/messages', icon: Mail, label: t('messages') });
        if (!isOfficeBound) {
          items.push({ href: '/admin/template', icon: FileSpreadsheet, label: t('template') });
          items.push({ href: '/admin/branding', icon: Palette, label: t('branding') });
          items.push({ href: '/admin/audit', icon: ClipboardList, label: t('audit') });
        }
      }
      return items;
    }
    if (role === 'analyst') {
      return [
        { href: '/analyst', icon: LayoutDashboard, label: t('dashboard') },
        { href: '/analyst/clients', icon: Users, label: t('clients') },
        // --- Finances ---
        { href: '/admin/transactions', icon: DollarSign, label: 'Transactions' },
        { href: '/admin/withdrawals', icon: Wallet, label: 'Retraits' },
        { href: '/analyst/payments', icon: CreditCard, label: 'Mes Paiements' },
        { href: '/analyst/my-ribs', icon: Banknote, label: 'Mes RIB' },
        { href: '/admin/advances', icon: Banknote, label: 'Avances de fonds' },
        { href: '/admin/mandates', icon: FileText, label: 'Mandats de gestion' },
        // --- Communication ---
        { href: '/messages', icon: Mail, label: t('messages') },
        { href: '/analyst/whatsapp-templates', icon: MessageSquare, label: 'WhatsApp Templates' },
        // --- Paramètres ---
        { href: '/analyst/settings', icon: Settings, label: 'Ma carte' },
      ];
    }
    
    // Client menu with categories - filtered by clientTemplate settings
    const isVisible = (key) => clientTemplate[key] !== false;
    
    const clientMenu = [];
    
    // ACCUEIL
    if (isVisible('menu_dashboard') || isVisible('menu_news')) {
      clientMenu.push({ type: 'category', label: 'ACCUEIL' });
      if (isVisible('menu_dashboard')) {
        clientMenu.push({ href: '/client', icon: LayoutDashboard, label: 'Tableau de bord' });
      }
      if (isVisible('menu_news')) {
        clientMenu.push({ href: '/client/news', icon: Newspaper, label: 'Actualités' });
      }
    }
    
    // INVESTISSEMENTS
    if (isVisible('menu_investments') || isVisible('menu_my_investments')) {
      clientMenu.push({ type: 'category', label: 'INVESTISSEMENTS' });
      if (isVisible('menu_investments')) {
        clientMenu.push({ href: '/client/placements', icon: Briefcase, label: 'Placement' });
      }
      if (isVisible('menu_my_investments')) {
        clientMenu.push({ href: '/client/my-investments', icon: FileText, label: 'Mes Investissements' });
      }
      clientMenu.push({ href: '/client/compare', icon: BarChart3, label: 'Comparer' });
    }
    
    // SIMULATION
    if (isVisible('menu_simulator') || isVisible('menu_simulation')) {
      clientMenu.push({ type: 'category', label: 'SIMULATION' });
      if (isVisible('menu_simulator')) {
        clientMenu.push({ href: '/client/simulator', icon: Calculator, label: 'Simulateur de gains' });
      }
      if (isVisible('menu_simulation')) {
        clientMenu.push({ href: '/client/products', icon: Package, label: 'Prévisualisation' });
      }
    }
    
    // ARBITRAGE
    if (isVisible('menu_trade')) {
      clientMenu.push({ type: 'category', label: 'ARBITRAGE' });
      clientMenu.push({ href: '/client/arbitrage', icon: TrendingUp, label: 'Salle des marchés' });
    }
    
    // BOURSE
    if (isVisible('menu_stock')) {
      clientMenu.push({ type: 'category', label: 'BOURSE' });
      if (isVisible('submenu_bourse_salle')) {
        clientMenu.push({ href: '/client/bourse', icon: TrendingUp, label: 'Salle des marchés' });
      }
      if (isVisible('submenu_bourse_epargnes')) {
        clientMenu.push({ href: '/client/portfolios', icon: Wallet, label: 'Les épargnes' });
      }
      if (isVisible('submenu_bourse_portefeuille')) {
        clientMenu.push({ href: '/client/portfolios', icon: Wallet, label: 'Mon portefeuille' });
      }
      if (isVisible('submenu_bourse_actions')) {
        clientMenu.push({ href: '/client/stock-portfolio', icon: BarChart3, label: 'Mes Actions', 'data-testid': 'sidebar-mes-actions' });
      }
    }
    
    // CRYPTO
    if (isVisible('menu_crypto') || isVisible('menu_wallets')) {
      clientMenu.push({ type: 'category', label: 'CRYPTO' });
      if (isVisible('menu_crypto')) {
        if (isVisible('submenu_crypto_salle')) {
          clientMenu.push({ href: '/client/crypto-buy', icon: TrendingUp, label: 'Acheter des cryptos' });
        }
        if (isVisible('submenu_crypto_portfolio') || !('submenu_crypto_portfolio' in clientTemplate)) {
          clientMenu.push({ href: '/client/crypto-portfolio', icon: Wallet, label: 'Mes Cryptos' });
        }
        if (isVisible('submenu_crypto_epargnes')) {
          clientMenu.push({ href: '/client/portfolios', icon: Wallet, label: 'Les épargnes' });
        }
      }
      if (isVisible('menu_wallets')) {
        clientMenu.push({ href: '/client/wallets', icon: Wallet, label: 'Mon wallet' });
      }
    }
    
    // MON ESPACE
    if (isVisible('menu_transactions') || isVisible('menu_messages') || isVisible('menu_documents') || isVisible('menu_profile')) {
      clientMenu.push({ type: 'category', label: 'MON ESPACE' });
      if (isVisible('menu_transactions')) {
        clientMenu.push({ href: '/client/transactions', icon: History, label: 'Historique' });
      }
      if (isVisible('menu_messages')) {
        clientMenu.push({ href: '/messages', icon: Mail, label: 'Messagerie' });
      }
      if (isVisible('menu_documents')) {
        clientMenu.push({ href: '/client/documents', icon: FileBox, label: 'Mes documents' });
      }
      if (isVisible('menu_profile')) {
        clientMenu.push({ href: '/client/profile', icon: Users, label: 'Profil' });
      }
    }
    
    return clientMenu;
  };

  const navItems = getNavItems();
  
  // Check if user is a client to show background image
  const isClient = user?.role === 'client';
  const isV2 = isClient && platformSettings?.design_version === 'v2';
  const isV3 = isClient && platformSettings?.design_version === 'v3';
  const isV4 = isClient && platformSettings?.design_version === 'v4';
  const showBgImage = isClient && !isV2 && !isV3 && !isV4 && branding?.clientBackgroundEnabled && (branding?.clientBackgroundUrl || branding?.clientBackgroundFile);
  const bgImageUrl = branding?.clientBackgroundFile || branding?.clientBackgroundUrl;
  const bgOpacity = branding?.clientBackgroundOpacity || 0.3;

  // V3/V4: delegate to dedicated layouts for client pages
  if (isV4 && !hideSidebar) {
    return <DashboardLayoutV4>{children}</DashboardLayoutV4>;
  }
  if (isV3 && !hideSidebar) {
    return <DashboardLayoutV3>{children}</DashboardLayoutV3>;
  }

  return (
    <div className={`min-h-screen relative ${showBgImage ? '' : 'bg-background'}`}>
      {/* Session Warning Modal */}
      <SessionWarning />
      
      {/* Background Image for Client */}
      {showBgImage && (
        <>
          {/* Base dark background */}
          <div className="fixed inset-0 bg-background" style={{ zIndex: -2 }} />
          {/* Image overlay */}
          <div 
            className="fixed inset-0 pointer-events-none"
            style={{ 
              zIndex: -1,
              backgroundImage: `url(${bgImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'fixed',
              opacity: bgOpacity
            }}
          />
        </>
      )}
      {mirrorMode && (
        <div className="mirror-banner" data-testid="mirror-mode-banner">
          <Eye className="w-4 h-4" />
          <span>Mode Vue Client — {user?.first_name} {user?.last_name}</span>
          <span className="text-xs opacity-70 ml-2">(Fermez l&apos;onglet pour quitter)</span>
        </div>
      )}

      {!hideSidebar && (
        <button className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded bg-card border border-border" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      )}

      {!hideSidebar && (
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} style={isV2 ? { background: '#0C0E14', borderRight: '1px solid rgba(255,255,255,0.04)' } : {}} data-testid="sidebar">
        <div className="flex flex-col h-full">
          <div className="p-6 border-b" style={isV2 ? { borderColor: 'rgba(255,255,255,0.04)' } : {}}>
            <button 
              onClick={() => navigate(getHomeRoute())}
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              title="Retour au dashboard"
              data-testid="logo-home-button"
            >
              {branding.logoUrl ? (
                <img 
                  src={branding.logoUrl} 
                  alt={branding.platformName || 'Logo'} 
                  style={{ height: `${branding.logoSize || 40}px`, width: 'auto' }}
                  className="object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div 
                  className="rounded flex items-center justify-center"
                  style={{ 
                    backgroundColor: currentTemplate?.colors?.primary || 'hsl(var(--primary))',
                    width: `${branding.logoSize || 40}px`,
                    height: `${branding.logoSize || 40}px`
                  }}
                >
                  <span 
                    className="font-bold" 
                    style={{ 
                      color: currentTemplate?.colors?.primaryForeground || 'hsl(var(--primary-foreground))',
                      fontSize: `${Math.max(12, (branding.logoSize || 40) / 3)}px`
                    }}
                  >
                    {branding.platformName?.substring(0, 2).toUpperCase() || 'PB'}
                  </span>
                </div>
              )}
              {branding.showPlatformName !== false && (
                <span className="font-semibold text-lg tracking-tight">{branding.platformName}</span>
              )}
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto" ref={el => {
            // Preserve sidebar scroll position across navigations
            if (el) {
              const saved = sessionStorage.getItem('sidebarScroll');
              if (saved && !el._restored) { el.scrollTop = parseInt(saved); el._restored = true; }
              el.onscroll = () => sessionStorage.setItem('sidebarScroll', String(el.scrollTop));
            }
          }}>
            {navItems.map((item, index) => {
              // Category header
              if (item.type === 'category') {
                return (
                  <div key={`cat-${index}`} className="pt-4 pb-2 px-4">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${isV2 ? 'text-[#D4AF37]/60' : 'text-muted-foreground'}`} style={isV2 ? { fontFamily: "'Outfit', sans-serif", fontSize: 10, letterSpacing: '0.12em' } : {}}>
                      {item.label}
                    </span>
                  </div>
                );
              }
              // Regular nav item
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              return (
                <Link 
                  key={item.href + index} 
                  to={item.href} 
                  onClick={() => setSidebarOpen(false)} 
                  className={`flex items-center gap-3 px-4 py-2.5 rounded transition-colors ${
                    isV2 
                      ? isActive 
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
                        : 'text-[#8B92A5] hover:text-[#F8FAFC] hover:bg-white/[0.03]'
                      : isActive 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                  }`} 
                  style={isActive && !isV2 ? { color: currentTemplate?.colors?.primary } : isV2 ? { fontFamily: "'Manrope', sans-serif", borderRadius: 12 } : {}}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm flex-1">{item.label}</span>
                  {item.href === '/admin/transactions' && pendingCounts.pending_transactions > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center">{pendingCounts.pending_transactions}</Badge>
                  )}
                  {item.href === '/admin/withdrawals' && pendingCounts.pending_withdrawals > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center">{pendingCounts.pending_withdrawals}</Badge>
                  )}
                  {item.href === '/messages' && pendingCounts.unread_messages > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center">{pendingCounts.unread_messages}</Badge>
                  )}
                  {(item.href === '/admin/payments' || item.href === '/analyst/payments') && pendingCounts.pending_payments > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center">{pendingCounts.pending_payments}</Badge>
                  )}
                  {item.href === '/admin/wallet-requests' && pendingCounts.pending_wallets > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center">{pendingCounts.pending_wallets}</Badge>
                  )}
                  {item.href === '/admin/advances' && pendingCounts.pending_advances > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center">{pendingCounts.pending_advances}</Badge>
                  )}
                  {item.href === '/admin/mandates' && pendingCounts.pending_mandates > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center">{pendingCounts.pending_mandates}</Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 space-y-4" style={isV2 ? { borderTop: '1px solid rgba(255,255,255,0.04)' } : { borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center gap-3 px-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={isV2 ? { background: 'rgba(212,175,55,0.12)' } : { background: 'rgba(212,175,55,0.2)' }}>
                <span style={{ color: '#D4AF37' }} className="font-semibold">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={isV2 ? { color: '#F8FAFC', fontFamily: "'Manrope', sans-serif" } : {}}>{user?.first_name} {user?.last_name}</p>
                <p className="text-xs" style={isV2 ? { color: '#8B92A5' } : { color: '#6b7280' }}>
                  {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin_ops' ? 'Admin Ops' : user?.role === 'bureau' ? 'Bureau' : user?.role === 'analyst' ? 'Analyste' : user?.role === 'client' ? 'Client' : user?.role?.replace('_', ' ')}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between px-2">
              {user?.role !== 'client' && (
                <button onClick={toggleDarkMode} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid="theme-toggle">
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
              )}
              <button onClick={() => changeLanguage(language === 'fr' ? 'en' : 'fr')} className="p-2 text-gray-400 hover:text-white transition-colors flex items-center gap-1" data-testid="language-toggle">
                <Globe className="w-5 h-5" /><span className="text-xs uppercase">{language}</span>
              </button>
              <button onClick={logout} className="p-2 text-gray-400 hover:text-red-500 transition-colors" data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>
      )}

      <main className={`main-content ${mirrorMode ? 'pb-16' : ''} ${hideSidebar ? '!ml-0' : ''}`} style={hideSidebar ? { marginLeft: 0 } : isV2 ? { background: '#080A0F' } : {}}>
        {!hideSidebar && (
        <header className="flex items-center justify-between mb-4 sm:mb-8">
          <h1 className="text-lg sm:text-2xl font-bold truncate" style={isV2 ? { fontFamily: "'Outfit', sans-serif", color: '#F8FAFC', fontWeight: 300 } : {}}>{navItems.find(item => location.pathname.startsWith(item.href))?.label || t('dashboard')}</h1>
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Clients Live - Admin/Analyst only */}
            {isAdmin && (
              <div className="relative" data-testid="clients-live-container">
                <button
                  onClick={() => setShowLiveDropdown(!showLiveDropdown)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors hover:bg-secondary/50"
                  data-testid="clients-live-btn"
                >
                  <Radio className={`w-4 h-4 ${liveClients.length > 0 ? 'text-green-500 animate-pulse' : 'text-muted-foreground'}`} />
                  <span className="text-muted-foreground font-medium">LIVE</span>
                  <Badge className={`h-5 min-w-5 flex items-center justify-center text-xs ${liveClients.length > 0 ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                    {liveClients.length}
                  </Badge>
                </button>
                {showLiveDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowLiveDropdown(false)} />
                    <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden" data-testid="clients-live-dropdown">
                      <div className="p-3 border-b border-border/50 flex items-center justify-between">
                        <span className="text-sm font-semibold">Clients en ligne</span>
                        <Badge variant="outline" className="text-xs">{liveClients.length} connecté{liveClients.length > 1 ? 's' : ''}</Badge>
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {liveClients.length === 0 ? (
                          <div className="p-6 text-center text-sm text-muted-foreground">
                            Aucun client connecté
                          </div>
                        ) : (
                          liveClients.map(client => {
                            const pageName = {
                              '/client': 'Tableau de bord',
                              '/client/dashboard': 'Tableau de bord',
                              '/client/placements': 'Placements',
                              '/client/investments': 'Investissements',
                              '/client/trading-room': 'Salle des marchés',
                              '/client/profile': 'Profil',
                              '/client/wallets': 'Wallets',
                              '/client/transactions': 'Transactions',
                              '/client/news': 'Actualités',
                              '/client/documents': 'Documents',
                              '/client/contracts': 'Contrats',
                              '/messages': 'Messagerie',
                            }[client.current_page] || client.current_page || 'Navigation';
                            return (
                              <Link
                                key={client.id}
                                to={`/admin/clients/${client.id}`}
                                onClick={() => setShowLiveDropdown(false)}
                                className="flex items-center gap-3 p-3 hover:bg-secondary/30 transition-colors border-b border-border/20 last:border-0"
                                data-testid={`live-client-${client.id}`}
                              >
                                <div className="relative">
                                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">
                                    {(client.first_name?.[0] || '')}{(client.last_name?.[0] || '')}
                                  </div>
                                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-card rounded-full" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{client.first_name} {client.last_name}</p>
                                  <p className="text-xs text-muted-foreground truncate">{pageName}</p>
                                </div>
                              </Link>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Quick Fund Access - Client only */}
            {isClient && !isV2 && (
              <>
              {clientCode && <span className="text-[10px] sm:text-xs text-muted-foreground font-mono hidden sm:inline">{clientCode}</span>}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full bg-card border hover:bg-accent" data-testid="quick-funds-btn">
                    <span className="text-sm hidden sm:inline">{user?.first_name}</span>
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </div>
                    <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/client/profile')} className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" /><span>Mon profil</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/client/transactions?action=deposit')} className="cursor-pointer">
                    <CreditCard className="mr-2 h-4 w-4 text-green-500" /><span>Déposer des fonds</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/client/transactions?action=withdraw')} className="cursor-pointer">
                    <Banknote className="mr-2 h-4 w-4 text-orange-500" /><span>Retirer des fonds</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/client/dashboard?action=transfer')} className="cursor-pointer">
                    <ArrowRightLeft className="mr-2 h-4 w-4 text-purple-500" /><span>Transfert interne</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { navigate('/login'); }} className="cursor-pointer text-red-500">
                    <LogOut className="mr-2 h-4 w-4" /><span>Déconnexion</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            )}
            {/* V2 Client Header Actions */}
            {isClient && isV2 && (
              <div className="flex items-center gap-1.5" data-testid="v2-header-actions">
                {clientCode && <span className="text-[10px] font-mono hidden sm:inline" style={{ color: '#8B92A5' }}>{clientCode}</span>}
                <button onClick={() => navigate('/client/transactions?action=deposit')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all hover:scale-105" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.15)' }} data-testid="v2-header-deposit">
                  <CreditCard className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Déposer</span>
                </button>
                <button onClick={() => navigate('/client/transactions?action=withdraw')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all hover:scale-105" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.15)' }} data-testid="v2-header-withdraw">
                  <Banknote className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Retirer</span>
                </button>
                <button onClick={() => navigate('/client/dashboard?action=transfer')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all hover:scale-105" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.15)' }} data-testid="v2-header-transfer">
                  <ArrowRightLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Transférer</span>
                </button>
                <button onClick={() => navigate('/client/profile')} className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-105 ml-1" style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.15)' }} data-testid="v2-header-profile">
                  <span className="text-xs font-semibold" style={{ color: '#D4AF37' }}>{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
                </button>
              </div>
            )}
            {/* Notifications bell - all roles */}
            <Link to="/notifications" className="relative p-2 text-gray-400 hover:text-white transition-colors" data-testid="notifications-btn">
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center bg-red-500 text-white text-xs">{unreadNotifications > 99 ? '99+' : unreadNotifications}</Badge>}
            </Link>
          </div>
        </header>
        )}
        <div className="animate-fade-in">{children}</div>
        {(user?.role === 'client' || mirrorMode) && (
          <footer
            className="mt-10 mx-auto max-w-4xl px-6 py-8 border-t border-border/20"
            data-testid="client-legal-footer"
          >
            <div className="space-y-4">
              {/* Logo */}
              {branding?.logoUrl && (
                <div>
                  <img 
                    src={branding.logoUrl} 
                    alt={platformSettings?.platform_name || ''} 
                    className="h-10 object-contain opacity-80"
                  />
                </div>
              )}
              {/* Legal notice / Regulation */}
              {platformSettings?.legal_notice && (() => {
                const lines = platformSettings.legal_notice.split('\n').filter(l => l.trim());
                const firstLine = lines[0] || '';
                const rest = lines.slice(1).join('\n');
                return (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground/80 tracking-wide">{firstLine}</p>
                    {rest && (
                      <p className="text-[11px] text-muted-foreground/60 leading-relaxed">
                        {rest}
                      </p>
                    )}
                  </div>
                );
              })()}
              {/* Privacy Policy & Terms link */}
              {platformSettings?.privacy_policy && (
                <button
                  onClick={() => setShowLegalPopup(true)}
                  className="text-[11px] text-primary/70 hover:text-primary underline mt-2"
                  data-testid="privacy-terms-link"
                >
                  Politique de confidentialité et conditions générales d'utilisation
                </button>
              )}
            </div>
          </footer>
        )}
      </main>

      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />}

      {/* Legal Popup */}
      <Dialog open={showLegalPopup} onOpenChange={setShowLegalPopup}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Politique de confidentialité et conditions générales d'utilisation</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {platformSettings?.privacy_policy && (
              <div className="prose prose-invert prose-sm max-w-none">
                {platformSettings.privacy_policy.split('\n\n').map((block, i) => {
                  const trimmed = block.trim();
                  if (!trimmed) return null;
                  if (trimmed.match(/^(ARTICLE|Article|SECTION|Section|\d+[\.\)–—])/)) {
                    return <h3 key={i} className="text-white font-semibold text-sm mt-5 mb-2 pb-2 border-b border-border/30">{trimmed}</h3>;
                  }
                  return <p key={i} className="text-sm text-muted-foreground leading-relaxed my-2">{trimmed}</p>;
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardLayout;
